import React, { useEffect, useRef, useState } from "react";
import { axiosMaster } from "../api/axiosInstance";

const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();
};

const AddEditEmployeeOptionalFields = ({ employeeData, onClose, onSuccess }) => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const groupId = storedUser?.groupId || 1;
  const userId = storedUser?.userId || 0;

  const [loading, setLoading] = useState(false);

  /* ================= STATE ================= */
  const [birthDate, setBirthDate] = useState("");
  const [anniversaryDate, setAnniversaryDate] = useState("");
  const [aadharCardNo, setAadharCardNo] = useState("");
  const [panNo, setPanNo] = useState("");
  const [policeVerificationDate, setPoliceVerificationDate] = useState("");
  const [policeVerificationNo, setPoliceVerificationNo] = useState("");
  const [referenceBy, setReferenceBy] = useState("");
  const [referenceMobileNo, setReferenceMobileNo] = useState("");
  const [relievingDate, setRelievingDate] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [landMark, setLandMark] = useState("");
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [pinCode, setPinCode] = useState("");

  /* ================= REFS ================= */
  const address1Ref = useRef(null);
  const address2Ref = useRef(null);
  const landMarkRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const pinCodeRef = useRef(null);

  const birthRef = useRef(null);
  const annivRef = useRef(null);
  const aadharRef = useRef(null);
  const panRef = useRef(null);
  const policeDateRef = useRef(null);
  const policeNoRef = useRef(null);
  const refByRef = useRef(null);
  const refMobileRef = useRef(null);
  const relievingRef = useRef(null);
  const saveRef = useRef(null);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!employeeData) return;
    setBirthDate(employeeData.birthDate?.substring(0, 10) || "");
    setAnniversaryDate(employeeData.anniversaryDate?.substring(0, 10) || "");
    setAadharCardNo(employeeData.aadharCardNo || "");
    setPanNo(employeeData.panNo || "");
    setPoliceVerificationDate(employeeData.policeVerificationDate?.substring(0, 10) || "");
    setPoliceVerificationNo(employeeData.policeVerificationNo || "");
    setReferenceBy(employeeData.referenceBy || "");
    setReferenceMobileNo(employeeData.referenceMobileNo || "");
    setRelievingDate(employeeData.relievingDate?.substring(0, 10) || "");
    setAddressLine1(employeeData.addressLine1 || "");
    setAddressLine2(employeeData.addressLine2 || "");
    setLandMark(employeeData.landMark || "");
    setCity(employeeData.city || "");
    setStateName(employeeData.state || "");
    setPinCode(employeeData.pinCode || "");
  }, [employeeData]);

  /* ================= AUTO FOCUS ================= */
  useEffect(() => {
    setTimeout(() => address1Ref.current?.focus(), 100);
  }, []);

  const handleFocusSelect = (ref) => setTimeout(() => ref?.current?.select(), 0);

  const formatPoliceNo = (value) => value.toUpperCase().replace(/[^A-Z0-9/-]/g, "").slice(0, 20);

  /* ================= KEYBOARD NAV ================= */
  const handleKeyDown = (e, nextRef, prevRef, options = {}) => {
    const { mobileCheck = false, isLast = false } = options;

    // Mobile Enter check
    if (mobileCheck && e.key === "Enter" && referenceMobileNo.length !== 10) {
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) nextRef.current.focus();
      else if (isLast && saveRef.current) saveRef.current.focus();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      if (prevRef?.current) prevRef.current.focus();
      else onClose?.(); // Close form if no previous field (Address Line 1)
    }
  };

  const handleBlurFormat = (setter) => (e) => setter(toTitleCase(e.target.value));

  const handleSubmit = async () => {
    setLoading(true);
    const payload = {
      empId: employeeData?.empId || 0,
      groupId,
      userId,
      empImageFileName: employeeData?.empImageFileName || "",
      birthDate: birthDate ? new Date(birthDate).toISOString() : null,
      anniversaryDate: anniversaryDate ? new Date(anniversaryDate).toISOString() : null,
      aadharCardNo,
      panNo,
      policeVerificationDate: policeVerificationDate ? new Date(policeVerificationDate).toISOString() : null,
      policeVerificationNo,
      referenceBy,
      referenceMobileNo,
      relievingDate: relievingDate ? new Date(relievingDate).toISOString() : null,
      addressLine1,
      addressLine2,
      landMark,
      city,
      state: stateName,
      pinCode,
    };

    try {
      await axiosMaster.post("/EmployeeMaster/UpdateEmployeeOptionalFields", payload);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error(err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-2" onClick={onClose}>
      <div
        className="bg-white w-full max-w-lg rounded-xl shadow-xl p-4 overflow-y-auto max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold mb-4">Employee Optional Fields</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">

          {/* Address Fields */}
          <InputText label="Address Line 1" value={addressLine1} setValue={setAddressLine1} refProp={address1Ref} nextRef={address2Ref} prevRef={null} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setAddressLine1)} handleFocusSelect={handleFocusSelect} />
          <InputText label="Address Line 2" value={addressLine2} setValue={setAddressLine2} refProp={address2Ref} nextRef={landMarkRef} prevRef={address1Ref} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setAddressLine2)} handleFocusSelect={handleFocusSelect} />
          <InputText label="Landmark" value={landMark} setValue={setLandMark} refProp={landMarkRef} nextRef={cityRef} prevRef={address2Ref} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setLandMark)} handleFocusSelect={handleFocusSelect} />
          <InputText label="City" value={city} setValue={setCity} refProp={cityRef} nextRef={stateRef} prevRef={landMarkRef} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setCity)} handleFocusSelect={handleFocusSelect} />
          <InputText label="State" value={stateName} setValue={setStateName} refProp={stateRef} nextRef={pinCodeRef} prevRef={cityRef} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setStateName)} handleFocusSelect={handleFocusSelect} />
          <InputText label="Pin Code" value={pinCode} setValue={(val) => setPinCode(val.replace(/\D/g, "").slice(0, 6))} refProp={pinCodeRef} nextRef={birthRef} prevRef={stateRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />

          {/* Personal / Documents */}
          <InputDate label="Birth Date" value={birthDate} setValue={setBirthDate} refProp={birthRef} nextRef={annivRef} prevRef={pinCodeRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputDate label="Anniversary Date" value={anniversaryDate} setValue={setAnniversaryDate} refProp={annivRef} nextRef={aadharRef} prevRef={birthRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputText label="Aadhar No" value={aadharCardNo} setValue={setAadharCardNo} refProp={aadharRef} nextRef={panRef} prevRef={annivRef} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setAadharCardNo)} handleFocusSelect={handleFocusSelect} />
          <InputText label="PAN No" value={panNo} setValue={setPanNo} refProp={panRef} nextRef={policeDateRef} prevRef={aadharRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputDate label="Police Verification Date" value={policeVerificationDate} setValue={setPoliceVerificationDate} refProp={policeDateRef} nextRef={policeNoRef} prevRef={panRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputText label="Police Verification No" value={policeVerificationNo} setValue={(val) => setPoliceVerificationNo(formatPoliceNo(val))} refProp={policeNoRef} nextRef={refByRef} prevRef={policeDateRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputText label="Reference By" value={referenceBy} setValue={setReferenceBy} refProp={refByRef} nextRef={refMobileRef} prevRef={policeNoRef} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setReferenceBy)} handleFocusSelect={handleFocusSelect} />
          <InputText label="Reference Mobile" value={referenceMobileNo} setValue={(val) => setReferenceMobileNo(val.replace(/\D/g, "").slice(0, 10))} refProp={refMobileRef} nextRef={relievingRef} prevRef={refByRef} handleKeyDown={(e) => handleKeyDown(e, relievingRef, refByRef, { mobileCheck: true })} handleFocusSelect={handleFocusSelect} />
          <InputDate label="Relieving Date" value={relievingDate} setValue={setRelievingDate} refProp={relievingRef} nextRef={saveRef} prevRef={refMobileRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />

        </div>

        <div className="flex justify-end gap-3 mt-4">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600">Cancel</button>
          <button ref={saveRef} onClick={handleSubmit} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const InputText = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleBlurFormat, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      ref={refProp}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlurFormat}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-2 py-2 text-sm"
    />
  </div>
);

const InputDate = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      ref={refProp}
      type="date"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-2 py-2 text-sm"
    />
  </div>
);

export default AddEditEmployeeOptionalFields;